import Cropper from 'react-easy-crop';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProfileEasterEggs from '../components/ProfileEasterEggs';
import { API_URL, fetchWithAuth, getImageUrl } from '../api';
// import Footer from '../components/Footer'; // (Opcional si lo eliminaste)
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale'; // Importante para fechas en español
import { 
  User, Calendar, Settings, Layers, 
  Phone, Mail, Award, Users, Activity, 
  ArrowLeft, Upload, X, LogOut, CheckCircle, Zap
} from 'react-feather';

// --- PLACEHOLDERS ---
//import MyPasses from '../components/ProfileDetails/MyPasses';
import MyEvents from '../components/ProfileDetails/MyEvents';
import ConfigProfile from '../components/ProfileDetails/ConfigProfile';

export default function Profile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser, logout } = useAuth();
  
  // State for Navigation
  const [activeTab, setActiveTab] = useState('profile'); 
  
  // Logic State
  const fileInputRef = useRef(null);
  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userStats, setUserStats] = useState(null);
  const [isFriend, setIsFriend] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const cropImageRef = useRef(null);
  
  // Friend request state
  const [friendRequestLoading, setFriendRequestLoading] = useState(false);
  const [showFriendRequestPanel, setShowFriendRequestPanel] = useState(false);
  const [friendRequestError, setFriendRequestError] = useState('');

  const isOwnProfile = !id || (currentUser && currentUser.id === parseInt(id));

  // Crop
  const [crop, setCrop] = useState({ x: 0, y: 0});
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Esta función guarda las coordenadas exactas cuando terminas de mover la foto
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Función auxiliar para recortar la imagen usando HTML5 Canvas
  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise(resolve => image.onload = resolve);

    const canvas = document.createElement('canvas');
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas está vacío'));
                return;
            }
            // Retorna un objeto File listo para subir
            resolve(new File([blob], "profile.jpg", { type: "image/jpeg" }));
        }, 'image/jpeg', 0.9); // 0.9 es la calidad
    });
  };

  // --- LOGIC: INITIALIZATION & FETCHING ---
  useEffect(() => {
    if (isOwnProfile && currentUser) {
      setProfileUser(prev => (!prev || prev.id !== currentUser.id) ? currentUser : prev);
    }
  }, [isOwnProfile, currentUser]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        const userId = id || currentUser?.id;
        if (!userId) { setError('Usuario no encontrado'); return; }

        const userData = await fetchWithAuth(`${API_URL}/users/${userId}`);
        
        if (isOwnProfile && currentUser?.profile_image_url) {
          userData.profile_image_url = currentUser.profile_image_url;
        }
        setProfileUser(userData);

        if (!isOwnProfile) {
          try {
            const friendCheck = await fetchWithAuth(`${API_URL}/users/${userId}/is-friend`);
            setIsFriend(friendCheck.is_friend);
          } catch (err) { setIsFriend(false); }
        } else {
          setIsFriend(true);
        }

        try {
          const statsData = await fetchWithAuth(`${API_URL}/users/${userId}/stats`);
          setUserStats(statsData);
        } catch (err) { console.log('Could not fetch stats:', err); }

      } catch (err) {
        setError(typeof err.message === 'string' ? err.message : 'Error cargando perfil');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) fetchProfileData();
    // eslint-disable-next-line
  }, [id, currentUser, isOwnProfile]);

  // --- LOGIC: IMAGE UPLOAD ---
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return alert('Solo imágenes válidas');
    if (file.size > 10 * 1024 * 1024) return alert('Máximo 10MB');

    const reader = new FileReader();
    reader.onload = (e) => {
      setCropImage(e.target.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropConfirm = async () => {
    // Verificamos que tengamos la imagen y las coordenadas del cropper
    if (!cropImage || !croppedAreaPixels) return; 

    try {
      setUploadingImage(true);

      // 1. Cargamos la imagen en memoria
      const img = new Image();
      img.src = cropImage;
      await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
      });

      // 2. Preparamos un Canvas de 500x500 (tu estándar)
      const canvas = document.createElement('canvas');
      canvas.width = 500; 
      canvas.height = 500;
      const ctx = canvas.getContext('2d');

      // 3. MAGIA: Dibujamos en el canvas basándonos EXACTAMENTE en el recorte de react-easy-crop
      ctx.drawImage(
        img,
        croppedAreaPixels.x,       // X del recorte
        croppedAreaPixels.y,       // Y del recorte
        croppedAreaPixels.width,   // Ancho del recorte
        croppedAreaPixels.height,  // Alto del recorte
        0,                         // X destino en el canvas
        0,                         // Y destino en el canvas
        500,                       // Forzar a ancho 500
        500                        // Forzar a alto 500
      );
      
      // 4. Convertimos el canvas a Blob de forma asíncrona y limpia
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error("No se pudo generar la imagen");

      // 5. Subimos al Backend (Tu código intacto)
      const formData = new FormData();
      formData.append('file', blob, 'profile-image.png');
      
      const response = await fetch(`${API_URL}/users/upload-profile-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      
      if (!response.ok) throw new Error('Error al subir la imagen al servidor');
      const data = await response.json();
      
      // 6. Actualizamos los estados visuales y locales
      setProfileUser(prev => ({ ...prev, profile_image_url: data.image_url }));
      
      if (isOwnProfile) {
        // Obtenemos el currentUser del local storage por si acaso para no sobreescribir nulos
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...storedUser, profile_image_url: data.image_url }));
      }
      
      setShowCropModal(false);
      setCropImage(null);

    } catch (err) { 
      alert(err.message || 'Error al procesar la imagen'); 
    } finally { 
      setUploadingImage(false); 
    }
  };

  const handleSendFriendRequest = async () => {
    if (!profileUser) return;
    try {
      setFriendRequestLoading(true);
      await fetchWithAuth(`${API_URL}/friends/request`, {
        method: 'POST',
        body: JSON.stringify({ username: profileUser.username })
      });
      setShowFriendRequestPanel(true);
      setTimeout(() => navigate('/friends'), 1600);
    } catch (err) {
      setFriendRequestError(err.message || 'Error al enviar solicitud');
    } finally {
      setFriendRequestLoading(false);
    }
  };

  // --- RENDER HELPERS ---
  
  if (!currentUser) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white">Login Required</div>;
  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white">Cargando...</div>;
  
  // Blocked Profile View
  if (!isOwnProfile && isFriend === false) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-[#ffffff05] p-10 rounded-3xl border border-white/10">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-800 to-black rounded-full flex items-center justify-center border border-white/10">
             {profileUser?.profile_image_url ? (
                 <img src={getImageUrl(profileUser.profile_image_url)} className="w-full h-full rounded-full object-cover opacity-50 grayscale" />
             ) : <User size={32} className="text-white/20"/>}
          </div>
          <h3 className="text-xl font-medium mb-2">Este perfil es privado</h3>
          <p className="text-gray-400 mb-8 text-sm">Agrega a @{profileUser?.username} a tus amigos para ver su contenido.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate(-1)} className="px-6 py-2 rounded-full border border-white/20 hover:bg-white/5 transition text-sm">Volver</button>
            <button onClick={handleSendFriendRequest} disabled={friendRequestLoading} className="px-6 py-2 rounded-full bg-white text-black font-medium hover:scale-105 transition text-sm">
                {friendRequestLoading ? 'Enviando...' : 'Agregar Amigo'}
            </button>
          </div>
        </div>
        {showFriendRequestPanel && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
                <CheckCircle size={40} className="mx-auto text-green-500 mb-4"/>
                <h3 className="text-white font-medium">Solicitud Enviada</h3>
            </div>
            </div>
        )}
      </div>
    );
  }

  // --- NAVIGATION ITEMS ---
  const navItems = [
    { id: 'profile', label: 'Mi Perfil', icon: User, allowed: true },
    //{ id: 'qr', label: 'Mis QR', icon: Layers, allowed: isOwnProfile },
    { id: 'events', label: 'Mis Eventos', icon: Calendar, allowed: isOwnProfile },
    { id: 'settings', label: 'Ajustes', icon: Settings, allowed: isOwnProfile },
  ];

  // --- CONTENT RENDERER ---
  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        // --- LOGICA DE NIVELES (GAMIFICACIÓN) ---
        const getLevelData = (count) => {
            const c = count || 0;
            // 0-2: Novato
            if (c < 3) return { 
                label: 'Novato', 
                color: 'text-gray-400', 
                nextLevel: 3, 
                nextLabel: 'Entusiasta',
                percent: (c / 3) * 100,
                icon: User
            };
            // 3-9: Entusiasta (Tu caso actual: 4)
            if (c < 10) return { 
                label: 'Entusiasta', 
                color: 'text-blue-400', 
                nextLevel: 10, 
                nextLabel: 'Juerguero',
                // (Actual - Base) / (Meta - Base)
                percent: ((c - 3) / (10 - 3)) * 100,
                icon: Zap
            };
            // 10-24: Juerguero
            if (c < 25) return { 
                label: 'Juerguero', 
                color: 'text-purple-400', 
                nextLevel: 25, 
                nextLabel: 'VIP',
                percent: ((c - 10) / (25 - 10)) * 100,
                icon: Activity
            };
            // 25-49: VIP
            if (c < 50) return { 
                label: 'VIP', 
                color: 'text-amber-400', 
                nextLevel: 50, 
                nextLabel: 'Leyenda',
                percent: ((c - 25) / (50 - 25)) * 100,
                icon: Award
            };
            // 50+: Leyenda
            return { 
                label: 'Leyenda', 
                color: 'text-yellow-500', 
                nextLevel: null, 
                nextLabel: 'Máximo',
                percent: 100,
                icon: Award
            };
        };

        const levelInfo = getLevelData(userStats?.events_assisted);
        const eventsNeeded = levelInfo.nextLevel ? levelInfo.nextLevel - (userStats?.events_assisted || 0) : 0;

        return (
            <div className="space-y-6 animate-fade-in">
                {/* 1. Header Card */}
                <div className="bg-[#ffffff03] border border-white/5 rounded-3xl p-8 flex flex-col md:flex-row items-center md:items-start gap-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-purple-900/10 blur-[100px] rounded-full pointer-events-none"/>

                    {/* Photo */}
                    <div className="relative group shrink-0">
                        <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-[#111] border-2 border-white/10 p-1 relative z-10">
                            <div className="w-full h-full rounded-full overflow-hidden bg-black relative">
                                {profileUser.profile_image_url ? (
                                    <img src={getImageUrl(profileUser.profile_image_url)} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-white/20">
                                        {profileUser.name?.charAt(0)}
                                    </div>
                                )}
                            </div>
                            
                            {isOwnProfile && (
                                <button 
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute bottom-1 right-1 bg-white text-black p-2 rounded-full hover:scale-110 transition shadow-lg z-20"
                                >
                                    <Upload size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="text-center md:text-left z-10 flex-1">
                        <h1 className="text-3xl md:text-4xl font-medium text-white tracking-tight mb-2">{profileUser.name}</h1>
                        <p className="text-gray-400 font-mono text-sm mb-4">@{profileUser.username}</p>
                        
                        <div className="flex flex-wrap justify-center md:justify-start gap-3">
                            {profileUser.role === 'admin' && (
                                <span className="px-3 py-1 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-full text-xs font-medium">Admin</span>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-2 md:mt-0">
                                <span className="flex items-center gap-1"><Users size={14}/> {userStats?.friends_count || 0} Amigos</span>
                                <span className="flex items-center gap-1"><Calendar size={14}/> {userStats?.events_assisted || 0} Eventos</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Detailed Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    
                    {/* --- TARJETA DE NIVEL (MEJORADA) --- */}
                    <div className="bg-[#ffffff03] border border-white/5 p-6 rounded-2xl hover:bg-[#ffffff05] transition relative overflow-hidden group">
                        {/* Barra de progreso inferior */}
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-white/5">
                            <div 
                                className={`h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-1000 ease-out`} 
                                style={{ width: `${levelInfo.percent}%` }}
                            />
                        </div>
                        
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2 text-gray-400">
                                <levelInfo.icon size={18} className={levelInfo.color} />
                                <span className="text-xs uppercase tracking-wider font-medium">Nivel de Fiesta</span>
                            </div>
                            {levelInfo.nextLevel && (
                                <span className="text-[10px] bg-white/5 px-2 py-0.5 rounded text-gray-400">
                                    {Math.round(levelInfo.percent)}% XP
                                </span>
                            )}
                        </div>

                        <div className="flex items-end gap-2 mb-2">
                             <p className={`text-2xl font-bold ${levelInfo.color} tracking-tight`}>
                                {levelInfo.label}
                             </p>
                        </div>
                        
                        {levelInfo.nextLevel ? (
                            <p className="text-xs text-gray-500">
                                Solo <span className="text-white font-medium">{eventsNeeded} eventos</span> más para ser <span className="text-gray-300">{levelInfo.nextLabel}</span>.
                            </p>
                        ) : (
                            <p className="text-xs text-amber-500/70">¡Has alcanzado el nivel máximo!</p>
                        )}
                    </div>

                    <div className="bg-[#ffffff03] border border-white/5 p-6 rounded-2xl hover:bg-[#ffffff05] transition">
                        <div className="flex items-center gap-3 mb-2 text-gray-400">
                            <Layers size={18} /> <span className="text-xs uppercase tracking-wider">Pases Activos</span>
                        </div>
                        <p className="text-2xl font-semibold text-white">{userStats?.active_passes || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">Listos para escanear.</p>
                    </div>

                    <div className="bg-[#ffffff03] border border-white/5 p-6 rounded-2xl hover:bg-[#ffffff05] transition">
                        <div className="flex items-center gap-3 mb-2 text-gray-400">
                            <Activity size={18} /> <span className="text-xs uppercase tracking-wider">Última Juerga</span>
                        </div>
                        <p className="text-xl font-semibold text-white">
                            {userStats?.last_attended_date 
                                ? format(parseISO(userStats.last_attended_date), 'd MMM yyyy', { locale: es }) 
                                : '---'}
                        </p>
                         <p className="text-xs text-gray-500 mt-1">
                             {userStats?.last_attended_date ? 'Fue una buena noche.' : 'Aún no registras asistencia.'}
                         </p>
                    </div>
                </div>

                {/* 3. Contact Info */}
                <div className="bg-[#ffffff03] border border-white/5 rounded-3xl p-8">
                    <h3 className="text-lg font-medium text-white mb-6">Información Personal</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between py-3 border-b border-white/5">
                            <span className="text-gray-500 text-sm flex items-center gap-2"><Mail size={16}/> Email</span>
                            <span className="text-white/90 text-sm">{profileUser.email}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b border-white/5">
                            <span className="text-gray-500 text-sm flex items-center gap-2"><Phone size={16}/> Teléfono</span>
                            <span className="text-white/90 text-sm">{profileUser.phone || 'No registrado'}</span>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b border-white/5">
                            <span className="text-gray-500 text-sm flex items-center gap-2"><User size={16}/> DNI</span>
                            <span className="text-white/90 text-sm">{profileUser.dni || '---'}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
      //case 'qr': return <MyPasses />;
      case 'events': return <MyEvents />;      
      case 'settings': return <ConfigProfile />;
      default: return null;
    }
  };

  return (
    <div className="flex h-screen bg-[#050505] pt-24 text-white font-sans flex flex-col lg:flex-row overflow-hidden">
      
      {/* --- SIDEBAR --- */}
      <div className="lg:w-60 mt-24 lg:h-screen lg:fixed lg:left-0 lg:top-0 lg:border-r border-white/5 bg-black z-20 w-full sticky top-0 border-b lg:border-b-0">
        <div className="h-full flex flex-col">

          {/* 2. NAVIGATION */}
          <div className="p-3 lg:p-3 flex-1">
            <nav className="flex lg:flex-col gap-[2px] overflow-x-auto lg:overflow-visible no-scrollbar">
              {navItems.filter(item => item.allowed).map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 whitespace-nowrap text-[13px] font-medium
                      ${isActive 
                        ? 'bg-white/10 text-white' 
                        : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-200'
                      }
                    `}
                  >
                    <item.icon size={16} strokeWidth={isActive ? 2 : 1.5} />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* 3. FOOTER / LOGOUT */}
          <div className="mt-auto hidden lg:flex flex-col p-3 border-t border-white/5">
            {isOwnProfile && (
              <button 
                onClick={logout} 
                className="flex items-center gap-3 px-3 py-2 rounded-md text-zinc-500 hover:bg-red-500/10 hover:text-red-400 transition-all w-full text-left text-[13px] group"
              >
                <LogOut size={16} className="group-hover:text-red-400" /> 
                Cerrar Sesión
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- MAIN CONTENT --- */}
      <div className="flex-1 lg:ml-72 relative h-full overflow-y-auto">
        <ProfileEasterEggs username={profileUser?.username} />
         <div className="fixed top-0 left-0 w-full h-full pointer-events-none">
            <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] bg-purple-900/5 blur-[120px] rounded-full"/>
         </div>

         <div className="relative z-10 p-6 lg:p-12 max-w-5xl mx-auto">
            <div className="lg:hidden mb-6">
                 {!isOwnProfile && (
                    <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 mb-4">
                        <ArrowLeft size={16}/> Atrás
                    </button>
                 )}
            </div>
             {renderContent()}
         </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
      
      {showCropModal && cropImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
          <div className="bg-[#111] border border-white/10 rounded-3xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-white">Ajustar Foto</h3>
              <button onClick={() => { setShowCropModal(false); setCropImage(null); }} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            
            {/* Contenedor del Cropper */}
            <div className="relative w-full mb-6 bg-black rounded-xl overflow-hidden aspect-square border border-white/10">
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1} // Fuerza a que sea cuadrado 1:1
                cropShape="round" // Hace la máscara circular automáticamente
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>

            {/* Slider para el Zoom (Opcional pero muy buena UX) */}
            <div className="mb-6 px-2">
                <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onChange={(e) => setZoom(e.target.value)}
                    className="w-full h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-white"
                />
            </div>

            <div className="flex gap-3">
              <button onClick={() => { setShowCropModal(false); setCropImage(null); }} className="flex-1 py-3 rounded-xl border border-white/10 text-white hover:bg-white/5 transition">Cancelar</button>
              <button onClick={handleCropConfirm} disabled={uploadingImage} className="flex-1 py-3 bg-white text-black rounded-xl font-medium hover:bg-gray-200 transition">
                {uploadingImage ? 'Guardando...' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}